/**
 * useEmojiVirtualizer.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Row-level virtual list math for the emoji grid.
 * Knows nothing about React rendering or the DOM — pure calculation.
 */

import { useMemo } from 'react';
import type { EmojiItem } from './useEmojiUsage';

export const CELL_SIZE = 36; // px width & height of one emoji button
export const CELL_GAP = 6;   // px gap between cells

export function useEmojiVirtualizer(params: {
  items: EmojiItem[];
  columns: number;
  scrollTop: number;
  viewportHeight: number;
}) {
  const { items, columns, scrollTop, viewportHeight } = params;

  return useMemo(() => {
    const rowHeight = CELL_SIZE + CELL_GAP;
    const totalRows = Math.ceil(items.length / columns);
    const visibleRows = Math.max(1, Math.ceil(viewportHeight / rowHeight));
    const OVERSCAN = 6;

    const startRow = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
    const endRow = Math.min(totalRows, startRow + visibleRows + OVERSCAN * 2);

    return {
      rowHeight,
      topSpacer: startRow * rowHeight,
      bottomSpacer: Math.max(0, (totalRows - endRow) * rowHeight),
      visibleItems: items.slice(startRow * columns, endRow * columns),
    };
  }, [items, columns, scrollTop, viewportHeight]);
}
